import { useCallback, useRef, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import store from "../State/Store/store";
import StorageManager from "../utils/StorageManager";
import {
  setWinAnimationsPlaying,
  setResetMainGameVisualEffects,
  setCounterAnimationStarted,
  setCounterAnimationFinished,
  setCriticalAnimationPlaying,
  setExpandingWildsOccurred,
  setSkipWinAnimation,
  setUserWantsToDismissWinAnimation,
  setSkipCounterAnimation,
  setIsWaitingForUserConfirmation,
  closeAllPopups,
  setSymbolPopupBlocked,
  openTurboSuggestionPopup,
  setTurboSuggestionPending,
  setShowHoldAndWinIntro,
  openHoldAndWinResumePopup,
  setShowHoldAndWinCollector,
  setShowHoldAndWinOutro,
  setShowMaxWin,
  openInfoPopup,
  clearFreeRoundsReloadPending,
  setIsAnimatedWin,
  setUserInterfaceDisabled,
  setShowFreeSpinIntro,
} from "../State/actions";
import { setFreeRoundsEnded } from "../State/Slices/freeRoundsSlice";
import {
  selectResetMainGameVisualEffects,
  selectCurrentRound,
  selectWinningLines,
  selectBet,
  selectSpinComplete,
  selectIsSpinning,
  selectTurboSuggestionPending,
  selectFreeSpinsActive,
  selectFeatureState,
  selectFlowState,
  selectLastCommand,
  selectRespinsLeft,
  selectFeatureSpinComplete,
  selectFeatureSpinActive,
  selectCriticalAnimationPlaying,
  selectHoldAndWinActive,
  selectHoldAndWinCurrentRound,
  selectAutoPlayActive,
  selectIntroPopupActive,
  selectIsHoldAndWinIntroReload,
  selectIsHoldAndWinMidFeatureReload,
  selectIsHoldAndWinCompletionReload,
  selectFreeRoundsReloadPending,
  selectIsFreeRoundsCompletionReload,
  selectFreeRoundsCompletionPending,
  selectWinAnimationsPlaying,
  selectCounterAnimationStarted,
  selectCounterAnimationFinished,
  selectFreeRoundsActive,
  selectErrorRecoveryTransitionActive,
  selectFreeRoundsTotalWin,
  selectCurrency,
  selectFreeSpinTriggered,
} from "../State/selectors";
import { SOUND_NAME } from "../sounds";
import { useAppActivity } from "../Contexts/AppActivityContext";
import { useReelRefs } from "../Contexts/ReelRefsContext";
import { useReelControl } from "../Contexts/ReelControlContext";
import { GAME_LAYOUT } from "../settings";
import { formatToCustomLocale } from "../utilities";
import { symbolData, COIN_SYMBOL_IDS, WILD_SYMBOL_ID } from "../SymbolData";
import {
  FEATURES_CONFIG,
  playCascadingExpandingWild,
  playFullReelExpandingWild,
} from "../featuresConfig";
import { soundManager } from "../SoundManager";

// Hook for managing win line animations, sound effects, and reel coordination
export const useWinController = () => {
  const {
    reelRefs,
    overlayReelRefs,
    winLinesContainerRef,
    winBoxContainerRef,
    dimReelSetRef,
    globalLineWinTextRef,
    lineWinTextRef,
  } = useReelRefs();

  const { spacebarTurboActive } = useReelControl();

  const dispatch = useDispatch();

  const spinComplete = useSelector(selectSpinComplete);
  const isSpinning = useSelector(selectIsSpinning);
  const currentRound = useSelector(selectCurrentRound);
  const winningLines = useSelector(selectWinningLines);
  const bet = useSelector(selectBet);
  const turboSuggestionPending = useSelector(selectTurboSuggestionPending);
  const freeSpinsActive = useSelector(selectFreeSpinsActive);
  const resetMainGameVisualEffects = useSelector(
    selectResetMainGameVisualEffects,
  );
  const featureState = useSelector(selectFeatureState);
  const flowState = useSelector(selectFlowState);
  const lastCommand = useSelector(selectLastCommand);
  const respinsLeft = useSelector(selectRespinsLeft);
  const featureSpinComplete = useSelector(selectFeatureSpinComplete);
  const featureSpinActive = useSelector(selectFeatureSpinActive);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const holdAndWinCurrentRound = useSelector(selectHoldAndWinCurrentRound);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const introPopupActive = useSelector(selectIntroPopupActive);
  const isHoldAndWinIntroReload = useSelector(selectIsHoldAndWinIntroReload);
  const isHoldAndWinMidFeatureReload = useSelector(
    selectIsHoldAndWinMidFeatureReload,
  );
  const isHoldAndWinCompletionReload = useSelector(
    selectIsHoldAndWinCompletionReload,
  );
  const freeRoundsReloadPending = useSelector(selectFreeRoundsReloadPending);
  const winAnimationsPlaying = useSelector(selectWinAnimationsPlaying);
  const counterAnimationStarted = useSelector(selectCounterAnimationStarted);
  const counterAnimationFinished = useSelector(selectCounterAnimationFinished);
  const isFreeRoundsActive = useSelector(selectFreeRoundsActive);
  const freeRoundsCount = useSelector((state) => state.freeRounds.count);
  const freeRoundsPerformed = useSelector(
    (state) => state.freeRounds.performed,
  );
  const freeRoundsTotalWin = useSelector(selectFreeRoundsTotalWin);
  const currency = useSelector(selectCurrency);
  const isFreeRoundsCompletionReload = useSelector(
    selectIsFreeRoundsCompletionReload,
  );
  const freeRoundsCompletionPending = useSelector(
    selectFreeRoundsCompletionPending,
  );
  const freeSpinTriggered = useSelector(selectFreeSpinTriggered);
  const errorRecoveryTransitionActive = useSelector(
    selectErrorRecoveryTransitionActive,
  );

  // Refs for state management and animation control
  const reelsetControllerRef = useRef(null);
  const currentTimeoutsRef = useRef([]);
  const pendingAnimationFramesRef = useRef([]);
  const isAnimatingRef = useRef(false);
  const isReadyRef = useRef(false);
  const animationCompletedRef = useRef(false);
  const isFirstAnimationRef = useRef(true);
  const spinCompleteRef = useRef(spinComplete);
  const currentRoundRef = useRef(currentRound);
  const winningLinesRef = useRef(winningLines);
  const winningDataRef = useRef(null);
  const lineIdsRef = useRef([]);
  const currentLineIndexRef = useRef(0);
  const dispatchRef = useRef(dispatch);
  const { isActive } = useAppActivity();
  const activityRef = useRef(true);
  const turboActiveRef = useRef(false);
  const winSoundPlayedRef = useRef(false);
  const expandingWildReelIndicesRef = useRef([]);
  const animatedRoundsRef = useRef(new Set());
  const holdAndWinOutroTriggeredRef = useRef(false);
  const holdAndWinTriggerSequenceActiveRef = useRef(false);
  const executeOnReelControllersRef = useRef(null);
  const executeOnOverlayReelControllersRef = useRef(null);
  const currentIntervalsRef = useRef([]);
  const expandingWildAnimationStateRef = useRef({});
  const currentSpineListenerRef = useRef(null);
  const masterSpineDurationRef = useRef(null); // Cached master spine win animation duration (ms)
  const prevWinPosSetRef = useRef(null); // Previous line's winning positions for diff-based transitions
  const introReloadWinProcessedRef = useRef(false);
  const introReloadWinStartedRef = useRef(false);

  // Memoize simple derived values
  const hasWins = useMemo(
    () => currentRound?.win && currentRound.win > 0,
    [currentRound?.win],
  );

  const hasScatterWin = (currentRound?.scatterCount || 0) > 0;

  const winType = currentRound?.winType || "";

  const isAnimatedWin = currentRound?.isAnimatedWin || false;

  /**
   * Detects if current round is a hold and win trigger round.
   * Uses universal featureState if available, falls back to legacy flowState.
   */
  const detectHoldAndWin = useCallback(() => {
    // Use universal featureState if available
    if (
      featureState &&
      featureState.type === "holdAndWin" &&
      featureState.phase === "intro"
    ) {
      return true;
    }
    // Legacy fallback
    return lastCommand === "spin" && flowState === "respin";
  }, [featureState, lastCommand, flowState]);

  /**
   * Detects if hold and win feature is ending (ready to show outro).
   * Uses universal featureState if available, falls back to legacy flowState.
   */
  const detectHoldAndWinOutro = useCallback(() => {
    // Use universal featureState if available
    if (
      featureState &&
      featureState.type === "holdAndWin" &&
      featureState.phase === "completion"
    ) {
      return (
        holdAndWinActive === true &&
        respinsLeft === 0 &&
        featureSpinComplete === true &&
        featureSpinActive === false
      );
    }
    // Legacy fallback
    return (
      holdAndWinActive === true &&
      lastCommand === "respin" &&
      flowState === "closed" &&
      respinsLeft === 0 &&
      featureSpinComplete === true &&
      featureSpinActive === false
    );
  }, [
    featureState,
    holdAndWinActive,
    lastCommand,
    flowState,
    respinsLeft,
    featureSpinComplete,
    featureSpinActive,
  ]);

  /**
   * Clears all active timeouts, intervals, and pending animation frames.
   * Prevents memory leaks and race conditions when animations are interrupted or component unmounts.
   */
  const clearAllTimeouts = useCallback(() => {
    const timeouts = currentTimeoutsRef.current;
    for (let i = 0; i < timeouts.length; i++) {
      clearTimeout(timeouts[i]);
    }
    currentTimeoutsRef.current = [];

    const intervals = currentIntervalsRef.current;
    for (let i = 0; i < intervals.length; i++) {
      clearInterval(intervals[i]);
    }
    currentIntervalsRef.current = [];

    const animationFrames = pendingAnimationFramesRef.current;
    for (let i = 0; i < animationFrames.length; i++) {
      cancelAnimationFrame(animationFrames[i]);
    }
    pendingAnimationFramesRef.current = [];

    // Remove any pending spine complete listener to prevent stale resolves
    if (currentSpineListenerRef.current) {
      const { spineInstance, listener } = currentSpineListenerRef.current;
      try {
        spineInstance?.state?.removeListener(listener);
      } catch {
        // spine may already be destroyed
      }
      currentSpineListenerRef.current = null;
    }
  }, []);

  /**
   * Shows coin win animations for Hold and Win trigger.
   * Collects coin positions, plays win animations, then waits for duration.
   */
  const showCoinAnimationsForHoldAndWin = useCallback(async () => {
    const coinPositions = [];
    const expandedReels = currentRoundRef.current?.reels;

    if (expandedReels && Array.isArray(expandedReels)) {
      for (let reelIndex = 0; reelIndex < expandedReels.length; reelIndex++) {
        const reel = expandedReels[reelIndex];
        if (!Array.isArray(reel)) continue;

        for (let symbolIndex = 0; symbolIndex < reel.length; symbolIndex++) {
          const symbolId = reel[symbolIndex];

          if (symbolId && COIN_SYMBOL_IDS.includes(symbolId)) {
            coinPositions.push([reelIndex, symbolIndex, symbolId]);
          }
        }
      }
    }

    if (coinPositions.length === 0) {
      return;
    }

    executeOnOverlayReelControllersRef.current((overlayReelController) => {
      overlayReelController?.hideAllAnimations();
    });

    executeOnReelControllersRef.current((controller) => {
      if (controller && controller.showAllSymbolTextures) {
        controller.showAllSymbolTextures();
      }
    });

    if (dimReelSetRef.current) {
      dimReelSetRef.current.dimReels();
    }
    for (let i = 0; i < coinPositions.length; i++) {
      const [reelIndex, symbolIndex] = coinPositions[i];
      const overlayReelController =
        reelsetControllerRef.current?.overlayReelControllers?.[reelIndex];
      const reelController =
        reelsetControllerRef.current?.reelControllers?.[reelIndex];

      if (!overlayReelController || !reelController) continue;

      const symbol = reelController.symbolsContainer.children[symbolIndex];

      if (symbol) {
        const actualSymbolId = symbol.currentSymbolId || symbol.symbolId;
        if (actualSymbolId !== undefined) {
          overlayReelController.showWinAnimation(symbolIndex, actualSymbolId);
        }
      }
    }

    soundManager.play(SOUND_NAME.coin_win);

    for (let i = 0; i < coinPositions.length; i++) {
      const [reelIndex, symbolIndex] = coinPositions[i];
      const reelController =
        reelsetControllerRef.current?.reelControllers?.[reelIndex];

      if (reelController && reelController.hideSymbolTexture) {
        reelController.hideSymbolTexture(symbolIndex);
      }
    }

    const displayDuration =
      FEATURES_CONFIG.WIN_ANIMATION_TIMING.LINE_CYCLE_DURATION;

    await new Promise((resolve) => {
      const timeoutId = setTimeout(() => {
        const index = currentTimeoutsRef.current.indexOf(timeoutId);
        if (index > -1) {
          currentTimeoutsRef.current.splice(index, 1);
        }
        resolve();
      }, displayDuration);
      currentTimeoutsRef.current.push(timeoutId);
    });
  }, [dimReelSetRef]);

  /**
   * Shows coin IDLE animations for Hold and Win trigger when Big Win is active.
   * Transitions coins to idle state (no sound) after global win completes.
   */
  const showCoinIdleAnimationsForBigWin = useCallback(async () => {
    const coinPositions = [];
    const expandedReels = currentRoundRef.current?.reels;

    if (expandedReels && Array.isArray(expandedReels)) {
      for (let reelIndex = 0; reelIndex < expandedReels.length; reelIndex++) {
        const reel = expandedReels[reelIndex];
        if (!Array.isArray(reel)) continue;

        for (let symbolIndex = 0; symbolIndex < reel.length; symbolIndex++) {
          const symbolId = reel[symbolIndex];

          // Check if this is a coin symbol
          if (symbolId && COIN_SYMBOL_IDS.includes(symbolId)) {
            coinPositions.push([reelIndex, symbolIndex, symbolId]);
          }
        }
      }
    }

    if (coinPositions.length === 0) {
      return;
    }

    executeOnOverlayReelControllersRef.current((overlayReelController) => {
      overlayReelController?.hideAllAnimations();
    });

    executeOnReelControllersRef.current((controller) => {
      if (controller && controller.showAllSymbolTextures) {
        controller.showAllSymbolTextures();
      }
    });

    if (dimReelSetRef.current) {
      dimReelSetRef.current.dimReels();
    }

    for (let i = 0; i < coinPositions.length; i++) {
      const [reelIndex, symbolIndex] = coinPositions[i];
      const overlayReelController =
        reelsetControllerRef.current?.overlayReelControllers?.[reelIndex];
      const reelController =
        reelsetControllerRef.current?.reelControllers?.[reelIndex];

      if (!overlayReelController || !reelController) continue;

      const symbol = reelController.symbolsContainer.children[symbolIndex];

      if (symbol) {
        const actualSymbolId = symbol.currentSymbolId || symbol.symbolId;
        if (actualSymbolId !== undefined) {
          overlayReelController.showIdleAnimation(symbolIndex, actualSymbolId);
        }
      }
    }

    for (let i = 0; i < coinPositions.length; i++) {
      const [reelIndex, symbolIndex] = coinPositions[i];
      const reelController =
        reelsetControllerRef.current?.reelControllers?.[reelIndex];

      if (reelController && reelController.hideSymbolTexture) {
        reelController.hideSymbolTexture(symbolIndex);
      }
    }

    await new Promise((resolve) => {
      const timeoutId = setTimeout(() => {
        const index = currentTimeoutsRef.current.indexOf(timeoutId);
        if (index > -1) {
          currentTimeoutsRef.current.splice(index, 1);
        }
        resolve();
      }, 500);
      currentTimeoutsRef.current.push(timeoutId);
    });
  }, [dimReelSetRef]);

  /**
   * Resets intro reload win processed flags when entering intro reload state.
   */
  useEffect(() => {
    if (isHoldAndWinIntroReload) {
      introReloadWinProcessedRef.current = false;
      introReloadWinStartedRef.current = false;
    }
  }, [isHoldAndWinIntroReload]);

  /**
   * Tracks when intro reload win animations start and complete.
   */
  useEffect(() => {
    if (isHoldAndWinIntroReload && hasWins) {
      if (
        (counterAnimationStarted || winAnimationsPlaying) &&
        !introReloadWinStartedRef.current
      ) {
        introReloadWinStartedRef.current = true;
      }

      if (
        introReloadWinStartedRef.current &&
        !counterAnimationStarted &&
        !winAnimationsPlaying
      ) {
        if (!introReloadWinProcessedRef.current) {
          introReloadWinProcessedRef.current = true;
        }
      }
    }
  }, [
    isHoldAndWinIntroReload,
    hasWins,
    counterAnimationStarted,
    winAnimationsPlaying,
  ]);

  /**
   * Handles Hold and Win reload scenarios.
   * Intro reload: Waits for intro popup to close and win animations to complete, then shows H&W intro.
   * Mid-feature reload: Waits for intro popup to close, then shows reload dialog.
   */
  useEffect(() => {
    if (
      FEATURES_CONFIG.HOLD_AND_WIN.ENABLED &&
      isHoldAndWinIntroReload &&
      !introPopupActive
    ) {
      if (hasWins && !introReloadWinProcessedRef.current) {
        return;
      }
      dispatch(setShowHoldAndWinIntro(true));
      return;
    }

    if (
      FEATURES_CONFIG.HOLD_AND_WIN.ENABLED &&
      isHoldAndWinMidFeatureReload &&
      !introPopupActive
    ) {
      dispatch(openHoldAndWinResumePopup());
      return;
    }
  }, [
    isHoldAndWinIntroReload,
    isHoldAndWinMidFeatureReload,
    introPopupActive,
    hasWins,
    isAnimatedWin,
    winAnimationsPlaying,
    counterAnimationStarted,
    dispatch,
  ]);

  /**
   * Handles Hold and Win completion reload scenario.
   * Shows outro/max win dialog immediately (skip collection animation).
   */
  useEffect(() => {
    if (
      FEATURES_CONFIG.HOLD_AND_WIN.ENABLED &&
      isHoldAndWinCompletionReload &&
      holdAndWinActive &&
      !introPopupActive
    ) {
      const showDialogTimeout = setTimeout(() => {
        const index = currentTimeoutsRef.current.indexOf(showDialogTimeout);
        if (index > -1) {
          currentTimeoutsRef.current.splice(index, 1);
        }

        const totalWin = holdAndWinCurrentRound?.win || 0;
        const maxWinThreshold =
          bet * FEATURES_CONFIG.HOLD_AND_WIN.MAX_WIN_MULTIPLIER;

        if (totalWin >= maxWinThreshold) {
          dispatch(setShowMaxWin(true));
        } else {
          dispatch(setShowHoldAndWinOutro(true));
        }
      }, 500);

      currentTimeoutsRef.current.push(showDialogTimeout);

      return () => {
        clearTimeout(showDialogTimeout);
        const index = currentTimeoutsRef.current.indexOf(showDialogTimeout);
        if (index > -1) {
          currentTimeoutsRef.current.splice(index, 1);
        }
      };
    }
  }, [
    isHoldAndWinCompletionReload,
    holdAndWinActive,
    introPopupActive,
    holdAndWinCurrentRound,
    bet,
    dispatch,
  ]);

  /**
   * Handles free rounds reload popup display.
   * Waits for intro popup and error recovery to complete, then shows appropriate message.
   * Blocks during Hold and Win or when free rounds completion is pending.
   */
  useEffect(() => {
    if (
      FEATURES_CONFIG.FREE_ROUNDS.ENABLED &&
      freeRoundsReloadPending &&
      !introPopupActive &&
      !isHoldAndWinIntroReload &&
      !isHoldAndWinMidFeatureReload &&
      !isHoldAndWinCompletionReload &&
      !holdAndWinActive &&
      !errorRecoveryTransitionActive &&
      !freeRoundsCompletionPending
    ) {
      const isLastRound = freeRoundsPerformed === freeRoundsCount;

      let popupData;
      if (isLastRound) {
        const formattedTotalWin = formatToCustomLocale(freeRoundsTotalWin);
        popupData = {
          key: "you_have_won_in_free_rounds",
          data: { N: `${formattedTotalWin} ${currency.toUpperCase()}` },
        };
      } else if (isFreeRoundsCompletionReload || freeRoundsPerformed === 0) {
        popupData = {
          key: "you_have_been_credited_with_free_rounds",
          data: { N: freeRoundsCount },
        };
      } else {
        popupData = {
          key: "you_have_free_rounds_left",
          data: { N: freeRoundsCount - freeRoundsPerformed },
        };
      }

      if (isLastRound) {
        dispatch(setFreeRoundsEnded());
      }

      dispatch(openInfoPopup(popupData));
      dispatch(clearFreeRoundsReloadPending());
    }
  }, [
    freeRoundsReloadPending,
    introPopupActive,
    isHoldAndWinIntroReload,
    isHoldAndWinMidFeatureReload,
    isHoldAndWinCompletionReload,
    holdAndWinActive,
    errorRecoveryTransitionActive,
    freeRoundsCompletionPending,
    freeRoundsCount,
    freeRoundsPerformed,
    isFreeRoundsCompletionReload,
    freeRoundsTotalWin,
    currency,
    dispatch,
  ]);

  /**
   * Main effect: Orchestrates win animations after spin completes.
   * Handles expanding wilds, normal wins, Big Win animations, and Hold and Win triggers.
   * Skips win processing when returning from Hold and Win to prevent duplicate animations.
   */
  useEffect(() => {
    if (
      !isReadyRef.current ||
      !reelsetControllerRef.current ||
      !spinComplete ||
      isAnimatingRef.current ||
      animationCompletedRef.current ||
      introPopupActive
    ) {
      return;
    }

    // Skip win processing when returning from Hold and Win (reload or natural play)
    if (
      FEATURES_CONFIG.HOLD_AND_WIN.ENABLED &&
      (isHoldAndWinCompletionReload || holdAndWinOutroTriggeredRef.current)
    ) {
      if (isFreeRoundsActive && !counterAnimationFinished) {
        dispatch(setCounterAnimationFinished(true));
      }
      animationCompletedRef.current = true;
      return;
    }

    if (FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && !holdAndWinActive) {
      const isHoldAndWinTrigger = detectHoldAndWin();

      if (isHoldAndWinTrigger) {
        animationCompletedRef.current = false;
        isFirstAnimationRef.current = true;
        winSoundPlayedRef.current = false;
        winningDataRef.current = null;
        lineIdsRef.current = [];
        currentLineIndexRef.current = 0;
        holdAndWinOutroTriggeredRef.current = false;

        dispatch(setWinAnimationsPlaying(false));
        dispatch(setCriticalAnimationPlaying(true));

        const hasScatterWinLocal = hasScatterWin;
        const expandingWildReels =
          FEATURES_CONFIG.EXPANDING_WILDS.ENABLED && !hasScatterWinLocal
            ? currentRound.expandingWildReels || []
            : [];
        const hasExpandingWilds = expandingWildReels.length > 0;

        const expandingWildReelIndices = expandingWildReels.map(
          ({ reelIndex }) => reelIndex,
        );
        expandingWildReelIndicesRef.current = expandingWildReelIndices;

        const hasRegularWins = hasWins && winningLines;
        const isAnimatedWinLocal = isAnimatedWin;

        const holdAndWinDelayId = setTimeout(() => {
          (async () => {
            holdAndWinTriggerSequenceActiveRef.current = true;
            try {
              if (hasExpandingWilds) {
                dispatch(setExpandingWildsOccurred(true));
                dispatch(setCriticalAnimationPlaying(true));

                // All expanding wilds play the same sound
                soundManager.play(SOUND_NAME.expanding_wild);

                const animationMode =
                  FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE;

                let animationPromises = [];

                if (animationMode === "full_reel") {
                  animationPromises = expandingWildReels.map(({ reelIndex }) =>
                    playFullReelExpandingWild(
                      reelIndex,
                      reelRefs,
                      overlayReelRefs,
                    ),
                  );
                } else if (animationMode === "cascade") {
                  animationPromises = expandingWildReels.map(
                    ({ reelIndex, wildPosition }) =>
                      playCascadingExpandingWild(
                        reelIndex,
                        wildPosition,
                        reelRefs,
                        overlayReelRefs,
                        currentTimeoutsRef,
                        dispatch,
                      ),
                  );
                }

                // Wait for all expanding wild "appear" animations to complete (event-driven)
                if (animationPromises.length > 0) {
                  await Promise.all(animationPromises);
                }

                // Hide cascade animations if needed
                if (animationMode === "cascade") {
                  executeOnOverlayReelControllersRef.current(
                    (overlayReelController) => {
                      overlayReelController?.hideAllAnimations();
                    },
                  );
                }

                dispatch(setCriticalAnimationPlaying(false));

                if (!isAnimatedWinLocal) {
                  dispatch(setCounterAnimationStarted(true));
                }
              }

              if (!hasExpandingWilds) {
                dispatch(setCriticalAnimationPlaying(false));
              }

              if (hasRegularWins) {
                const winningData =
                  reelsetControllerRef.current.matchHorizontalWinningLines(
                    winningLines,
                    currentRound.wins,
                  );

                if (winningData && Object.keys(winningData).length > 0) {
                  dispatch(setIsAnimatedWin(isAnimatedWinLocal));

                  if (!hasExpandingWilds && !isAnimatedWinLocal) {
                    dispatch(setCounterAnimationStarted(true));
                  }

                  if (isAnimatedWinLocal) {
                    dispatch(closeAllPopups());
                    dispatch(setSymbolPopupBlocked(true));
                    dispatch(setWinAnimationsPlaying(true));
                  }

                  await showAllWinningSymbolsForHoldAndWinTriggerRef.current(
                    winningData,
                    hasExpandingWilds,
                  );

                  const wins = currentRoundRef.current?.wins;
                  const winsHash =
                    Array.isArray(wins) && wins.length > 0
                      ? JSON.stringify(wins)
                      : null;
                  const roundId = currentRoundRef.current?.id || "unknown";
                  const uniqueAnimationId = `${roundId}-${winsHash}`;
                  animatedRoundsRef.current.add(uniqueAnimationId);
                }
              }

              if (isAnimatedWinLocal && hasRegularWins) {
                await showCoinIdleAnimationsForBigWin();

                await new Promise((resolve) => {
                  const checkInterval = setInterval(() => {
                    const state = dispatch((dispatch, getState) => getState());
                    const isStillPlaying = selectWinAnimationsPlaying(state);

                    if (!isStillPlaying) {
                      const index =
                        currentIntervalsRef.current.indexOf(checkInterval);
                      if (index > -1) {
                        currentIntervalsRef.current.splice(index, 1);
                      }
                      clearInterval(checkInterval);
                      resolve();
                    }
                  }, 100);
                  currentIntervalsRef.current.push(checkInterval);
                });
              }

              if (
                FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
                FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE ===
                  "full_reel" &&
                expandingWildReelIndicesRef.current.length > 0
              ) {
                expandingWildReelIndicesRef.current.forEach((reelIndex) => {
                  const overlayReelRef = overlayReelRefs.current?.[reelIndex];
                  overlayReelRef?.playExpandingWildDarkAnimation?.();
                  expandingWildAnimationStateRef.current[reelIndex] = false;
                });
              }

              await showCoinAnimationsForHoldAndWin();
              dispatch(setShowHoldAndWinIntro(true));
            } finally {
              holdAndWinTriggerSequenceActiveRef.current = false;
            }
          })();
        }, FEATURES_CONFIG.WIN_ANIMATION_TIMING.WIN_LINES_DELAY);

        currentTimeoutsRef.current.push(holdAndWinDelayId);
        return;
      }
    }

    if (FEATURES_CONFIG.HOLD_AND_WIN.ENABLED) {
      const isHoldAndWinOutro = detectHoldAndWinOutro();

      if (isHoldAndWinOutro && !criticalAnimationPlaying) {
        dispatch(setCriticalAnimationPlaying(true));
      }

      if (isHoldAndWinOutro && !holdAndWinOutroTriggeredRef.current) {
        holdAndWinOutroTriggeredRef.current = true;

        const holdAndWinCollectorTimeoutId = setTimeout(() => {
          dispatch(setShowHoldAndWinCollector(true));
        }, 1000);

        currentTimeoutsRef.current.push(holdAndWinCollectorTimeoutId);
        return;
      }
    }

    if (FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && holdAndWinActive) {
      return;
    }

    if (!hasWins || !winningLines) {
      return;
    }

    const winTypeLocal = winType;
    const isAnimatedWinLocal = isAnimatedWin;
    const hasScatterWinLocal = hasScatterWin;

    const expandingWildReels =
      FEATURES_CONFIG.EXPANDING_WILDS.ENABLED && !hasScatterWinLocal
        ? currentRound.expandingWildReels || []
        : [];
    const hasExpandingWilds = expandingWildReels.length > 0;

    const expandingWildReelIndices = expandingWildReels.map(
      ({ reelIndex }) => reelIndex,
    );
    expandingWildReelIndicesRef.current = expandingWildReelIndices;

    const wins = currentRound?.wins;
    const winsHash =
      Array.isArray(wins) && wins.length > 0 ? JSON.stringify(wins) : null;
    const roundId = currentRound?.id || "unknown";
    const uniqueAnimationId = `${roundId}-${winsHash}`;

    if (animatedRoundsRef.current.has(uniqueAnimationId)) {
      // Mark animation as completed to unblock UI
      animationCompletedRef.current = true;
      return;
    }

    animatedRoundsRef.current.add(uniqueAnimationId);

    // Lock immediately to prevent effect re-entry during setTimeout delay
    isAnimatingRef.current = true;

    // FREE SPIN TRIGGER: Disable entire UI immediately so user can't interact
    // during win processing of a trigger round (3+ scatters)
    if (FEATURES_CONFIG.FREE_SPINS.ENABLED && freeSpinTriggered) {
      dispatch(setUserInterfaceDisabled(true));
      dispatch(setCriticalAnimationPlaying(true));
    }

    // CRITICAL: Block spin button IMMEDIATELY before the WIN_LINES_DELAY
    // Without this, there's a 200ms gap where counterAnimationStarted is false
    // and the user can click spin before win processing starts
    dispatch(setCounterAnimationStarted(true));
    // Also hold WinDisplay hidden during the delay — it checks !criticalAnimationPlaying,
    // so it won't show until setCriticalAnimationPlaying(false) fires inside the setTimeout
    dispatch(setCriticalAnimationPlaying(true));

    setTimeout(() => {
      if (hasExpandingWilds) {
        dispatch(setExpandingWildsOccurred(true));
        dispatch(setCriticalAnimationPlaying(true));

        // All expanding wilds play the same sound
        soundManager.play(SOUND_NAME.expanding_wild);

        const animationMode = FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE;

        (async () => {
          let animationPromises = [];

          if (animationMode === "full_reel") {
            animationPromises = expandingWildReels.map(({ reelIndex }) =>
              playFullReelExpandingWild(reelIndex, reelRefs, overlayReelRefs),
            );
          } else if (animationMode === "cascade") {
            animationPromises = expandingWildReels.map(
              ({ reelIndex, wildPosition }) =>
                playCascadingExpandingWild(
                  reelIndex,
                  wildPosition,
                  reelRefs,
                  overlayReelRefs,
                  currentTimeoutsRef,
                  dispatch,
                ),
            );
          }

          // Wait for all expanding wild "appear" animations to complete (event-driven)
          if (animationPromises.length > 0) {
            await Promise.all(animationPromises);
          }

          // Hide cascade animations if needed
          if (animationMode === "cascade") {
            executeOnOverlayReelControllersRef.current(
              (overlayReelController) => {
                overlayReelController?.hideAllAnimations();
              },
            );
          }

          dispatch(setCriticalAnimationPlaying(false));

          const winningData =
            reelsetControllerRef.current.matchHorizontalWinningLines(
              winningLines,
              currentRound.wins,
            );

          if (winningData && Object.keys(winningData).length > 0) {
            dispatch(setIsAnimatedWin(isAnimatedWinLocal));
            dispatch(setCounterAnimationStarted(true));

            if (FEATURES_CONFIG.EXPANDING_WILDS.SHOW_ALL_WINS_BEFORE_CYCLING) {
              if (winTypeLocal && !winSoundPlayedRef.current) {
                if (isAnimatedWinLocal) {
                  winSoundPlayedRef.current = true;
                }
              }

              if (isAnimatedWinLocal) {
                dispatch(closeAllPopups());
                dispatch(setSymbolPopupBlocked(true));
                dispatch(setWinAnimationsPlaying(true));
              }

              const shouldProceed = await showAllWinningSymbolsOnceRef.current(
                winningData,
                true,
                expandingWildReelIndices,
                winTypeLocal,
                isAnimatedWinLocal,
              );

              if (shouldProceed && !turboActiveRef.current) {
                startWinningLinesAnimationRef.current(winningData);
              } else {
                if (
                  FEATURES_CONFIG.WIN_LINES.ENABLED &&
                  winLinesContainerRef.current
                ) {
                  winLinesContainerRef.current.hideWinLine();
                }
                if (globalLineWinTextRef?.current) {
                  globalLineWinTextRef.current.hide();
                }
                // NEW - unified line win text
                if (lineWinTextRef?.current) {
                  lineWinTextRef.current.hide();
                }
              }
            }

            if (!FEATURES_CONFIG.EXPANDING_WILDS.SHOW_ALL_WINS_BEFORE_CYCLING) {
              if (isAnimatedWinLocal) {
                dispatch(closeAllPopups());
                dispatch(setSymbolPopupBlocked(true));
                dispatch(setWinAnimationsPlaying(true));
              }
            }
          }
        })();
      } else {
        const winningData =
          reelsetControllerRef.current.matchHorizontalWinningLines(
            winningLines,
            currentRound.wins,
          );

        if (!winningData || Object.keys(winningData).length === 0) {
          return;
        }

        dispatch(setCriticalAnimationPlaying(true));

        const shouldShowAllWins = FEATURES_CONFIG.SHOW_ALL_WINS.ENABLED;
        const isScatterWin = currentRound?.type === "feature";
        const skipShowAllWins =
          isScatterWin && FEATURES_CONFIG.SHOW_ALL_WINS.EXCLUDE_SCATTER;

        (async () => {
          dispatch(setCriticalAnimationPlaying(false));
          dispatch(setIsAnimatedWin(isAnimatedWinLocal));
          dispatch(setCounterAnimationStarted(true));

          let shouldProceed = true;

          if (shouldShowAllWins && !skipShowAllWins) {
            if (winTypeLocal && !winSoundPlayedRef.current) {
              if (isAnimatedWinLocal) {
                winSoundPlayedRef.current = true;
              }
            }

            if (isAnimatedWinLocal) {
              dispatch(closeAllPopups());
              dispatch(setSymbolPopupBlocked(true));
              dispatch(setWinAnimationsPlaying(true));
            }

            shouldProceed = await showAllWinningSymbolsOnceRef.current(
              winningData,
              false,
              [],
              winTypeLocal,
              isAnimatedWinLocal,
            );
          }

          if (shouldProceed) {
            // When show-all is skipped, release the animation lock manually
            // (showAllWinningSymbolsOnce normally does this at its end)
            if (!shouldShowAllWins || skipShowAllWins) {
              isAnimatingRef.current = false;
            }
            startWinningLinesAnimationRef.current(winningData);
          } else {
            if (
              FEATURES_CONFIG.WIN_LINES.ENABLED &&
              winLinesContainerRef.current
            ) {
              winLinesContainerRef.current.hideWinLine();
            }
            if (globalLineWinTextRef?.current) {
              globalLineWinTextRef.current.hide();
            }
            // NEW - unified line win text
            if (lineWinTextRef?.current) {
              lineWinTextRef.current.hide();
            }
          }

          if (!shouldShowAllWins || skipShowAllWins) {
            // Play win sound for non-animated wins when show-all is skipped
            if (!isAnimatedWinLocal && SOUND_NAME.win) {
              soundManager.play(SOUND_NAME.win);
            }
            if (isAnimatedWinLocal) {
              dispatch(closeAllPopups());
              dispatch(setSymbolPopupBlocked(true));
              dispatch(setWinAnimationsPlaying(true));
            }
          }
        })();
      }
    }, FEATURES_CONFIG.WIN_ANIMATION_TIMING.WIN_LINES_DELAY);

    return () => clearAllTimeouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    spinComplete,
    currentRound,
    winningLines,
    reelRefs,
    overlayReelRefs,
    isSpinning,
    dispatch,
    detectHoldAndWin,
    detectHoldAndWinOutro,
    holdAndWinActive,
    introPopupActive,
    isHoldAndWinCompletionReload,
  ]);

  /**
   * Executes a callback function on each reel controller.
   */
  const executeOnReelControllers = useCallback(
    (callback) => {
      const reels = reelRefs.current;
      if (!reels) return;

      for (let i = 0; i < reels.length; i++) {
        const controller = reels[i]?.getReelController();
        if (controller) {
          callback(controller, i);
        }
      }
    },
    [reelRefs],
  );

  /**
   * Executes a callback function on each overlay reel controller.
   */
  const executeOnOverlayReelControllers = useCallback(
    (callback) => {
      const overlayReels = overlayReelRefs.current;
      if (!overlayReels) return;

      for (let i = 0; i < overlayReels.length; i++) {
        const controller = overlayReels[i]?.getOverlayReelController();
        if (controller) {
          callback(controller, i);
        }
      }
    },
    [overlayReelRefs],
  );

  useEffect(() => {
    executeOnReelControllersRef.current = executeOnReelControllers;
    executeOnOverlayReelControllersRef.current =
      executeOnOverlayReelControllers;
  }, [executeOnReelControllers, executeOnOverlayReelControllers]);

  /**
   * Resets all visual effects and animations to their default state.
   * Hides win texts, animations, restores symbol textures, and clears dimming overlay.
   * Preserves landing/idle animations when Hold and Win is active.
   */
  const resetAllEffects = useCallback(() => {
    if (dimReelSetRef.current) {
      dimReelSetRef.current.undimReels();
    }

    if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
      winLinesContainerRef.current.hideWinLine();
    }

    if (FEATURES_CONFIG.WIN_BOX.ENABLED && winBoxContainerRef.current) {
      winBoxContainerRef.current.hideWinBoxes();
    }

    executeOnOverlayReelControllersRef.current((overlayReelController) => {
      if (overlayReelController) {
        if (!holdAndWinActive) {
          if (overlayReelController.hideAllAnimations) {
            overlayReelController.hideAllAnimations();
          }
        }

        if (
          FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
          overlayReelController.isExpandingWildVisible
        ) {
          overlayReelController.forceStopExpandingWildFullReel?.();
        }
      }
    });

    executeOnReelControllersRef.current((controller) => {
      if (controller && controller.showAllSymbolTextures) {
        controller.showAllSymbolTextures();
      }
    });

    // NEW - unified line win text
    if (lineWinTextRef?.current) {
      lineWinTextRef.current.hide();
    }

    if (globalLineWinTextRef?.current) {
      globalLineWinTextRef.current.hide();
    }

    // Clear previous win positions for diff tracking
    prevWinPosSetRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalLineWinTextRef, lineWinTextRef, holdAndWinActive]);

  /**
   * Matches winning line data with game layout winning lines.
   * Groups multiple wins on the same line together and returns structured data for animation.
   */
  const matchHorizontalWinningLines = useCallback((winningLines, wins) => {
    const result = {};
    if (!Array.isArray(wins)) {
      return result;
    }

    for (let i = 0; i < wins.length; i++) {
      const win = wins[i];
      const { id, positions, symbol, win: winAmount } = win;

      if (!Array.isArray(positions)) {
        continue;
      }

      if (!result[i]) {
        result[i] = {
          line: id != null ? winningLines[id] : null,  // Scatter wins have no line (id is null)
          positions: [],
          symbol,
          win: winAmount,
          lineId: id, // Preserve original line index for win_line spine animation
          isScatter: !!win.isScatter, // Preserve scatter flag from RGS
        };
      }
      result[i].positions.push(...positions);
    }

    return result;
  }, []);

  /**
   * Syncs refs with current state values for use in async operations.
   */
  useEffect(() => {
    spinCompleteRef.current = spinComplete;
    currentRoundRef.current = currentRound;
    winningLinesRef.current = winningLines;
    dispatchRef.current = dispatch;
    activityRef.current = isActive;
    turboActiveRef.current = spacebarTurboActive || autoPlayActive;
  }, [
    spinComplete,
    currentRound,
    winningLines,
    dispatch,
    isActive,
    spacebarTurboActive,
    autoPlayActive,
    holdAndWinActive,
  ]);

  /**
   * Initializes the reelset controller object once on component mount.
   * Creates lazy-loaded getters for controllers and attaches utility methods.
   */
  useEffect(() => {
    if (reelsetControllerRef.current) {
      return;
    }

    reelsetControllerRef.current = {
      _reelControllers: null,
      _overlayReelControllers: null,

      get reelControllers() {
        if (!this._reelControllers) {
          this._reelControllers =
            reelRefs.current
              ?.map((reel) => reel?.getReelController())
              .filter(Boolean) || [];
        }
        return this._reelControllers;
      },

      get overlayReelControllers() {
        if (!this._overlayReelControllers) {
          this._overlayReelControllers =
            overlayReelRefs.current
              ?.map((overlayReel) => overlayReel?.getOverlayReelController())
              .filter(Boolean) || [];
        }
        return this._overlayReelControllers;
      },

      executeOnReelControllers,
      executeOnOverlayReelControllers,
      resetAllEffects,
      matchHorizontalWinningLines,
    };

    isReadyRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Cycles through winning lines one by one, displaying animations and win amounts.
   * Shows win text, restores textures for non-winning symbols, and plays animations for winning symbols.
   */
  const cycleThroughLines = useCallback(
    async (
      winningData,
      lineIds,
      isFirstEverCycle = false,
      startIndex = 0,
      onFirstLineComplete = null,
    ) => {
      for (let index = startIndex; index < lineIds.length; index++) {
        if (!isAnimatingRef.current) {
          break;
        }

        const lineId = lineIds[index];
        currentLineIndexRef.current = index;
        const data = winningData[lineId];

        if (!data) {
          continue;
        }

        // Play win_line sound only for numbered lines (scatter has null id)
        if (
          FEATURES_CONFIG.WIN_LINES.ENABLED &&
          SOUND_NAME.win_line &&
          data.lineId !== undefined &&
          data.lineId !== null
        ) {
          soundManager.play(SOUND_NAME.win_line);
        }

        // Find a target reel for positioning (prefer middle, then second reel)
        let targetReelIndex = null;

        for (let i = 0; i < data.positions.length; i++) {
          const [reelIndex] = data.positions[i];
          if (reelIndex === GAME_LAYOUT.MIDDLE_REEL) {
            targetReelIndex = GAME_LAYOUT.MIDDLE_REEL;
            break;
          }
        }

        if (targetReelIndex === null) {
          for (let i = 0; i < data.positions.length; i++) {
            const [reelIndex] = data.positions[i];
            if (reelIndex === GAME_LAYOUT.SECOND_REEL) {
              targetReelIndex = GAME_LAYOUT.SECOND_REEL;
              break;
            }
          }
        }

        // Hide previous line's win text before showing next
        if (lineWinTextRef?.current) {
          lineWinTextRef.current.hide();
        }
        if (globalLineWinTextRef?.current) {
          globalLineWinTextRef.current.hide();
        }

        // Show win amount for this line (works for all line types including scatter)
        if (data.win !== undefined && lineWinTextRef?.current) {
          lineWinTextRef.current.showWinAmount(data.win);
        }

        const currentLinePositions = data.positions.map(
          ([reelIndex, symbolIndex]) => {
            const reelController =
              reelsetControllerRef.current.reelControllers[reelIndex];
            const symbol =
              reelController?.symbolsContainer.children[symbolIndex];
            const symbolId = symbol?.currentSymbolId || symbol?.symbolId;
            return [reelIndex, symbolIndex, symbolId];
          },
        );

        // Pre-compute winning position Set for O(1) lookups
        const winPosSet = new Set(
          currentLinePositions.map(([r, s]) => `${r}_${s}`),
        );
        const reelsInCurrentLine = new Set(
          currentLinePositions.map(([reelIndex]) => reelIndex),
        );

        reelsetControllerRef.current.executeOnOverlayReelControllers(
          (overlayReelController) => {
            if (!overlayReelController?.isExpandingWildVisible) {
              overlayReelController?.hideAllAnimationsExcept(
                currentLinePositions,
              );
            }
          },
        );

        if (
          FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
          FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel" &&
          expandingWildReelIndicesRef.current.length > 0
        ) {
          const reelsArray = Array.from(reelsInCurrentLine).sort(
            (a, b) => a - b,
          );
          let rightmostConnected = -1;
          for (let i = 0; i < reelsArray.length; i++) {
            const currentReel = reelsArray[i];

            if (i === 0 && currentReel > 1) {
              break;
            }

            if (i > 0 && currentReel !== reelsArray[i - 1] + 1) {
              break;
            }

            rightmostConnected = currentReel;
          }

          expandingWildReelIndicesRef.current.forEach((wildReelIndex) => {
            const overlayReelRef = overlayReelRefs.current?.[wildReelIndex];
            const wildReelHasWinningPosition =
              reelsInCurrentLine.has(wildReelIndex);
            const shouldBeBright =
              wildReelHasWinningPosition && rightmostConnected >= wildReelIndex;
            const currentlyBright =
              expandingWildAnimationStateRef.current[wildReelIndex] ?? true;

            if (shouldBeBright && !currentlyBright) {
              overlayReelRef?.playExpandingWildWinAnimation?.();
              expandingWildAnimationStateRef.current[wildReelIndex] = true;
            } else if (!shouldBeBright && currentlyBright) {
              overlayReelRef?.playExpandingWildDarkAnimation?.();
              expandingWildAnimationStateRef.current[wildReelIndex] = false;
            }
          });
        }

        if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
          winLinesContainerRef.current.hideWinLine();
        }

        if (FEATURES_CONFIG.WIN_BOX.ENABLED && winBoxContainerRef.current) {
          winBoxContainerRef.current.hideWinBoxes();
        }

        // Diff-based texture transitions — only mutate symbols that changed between lines
        const prevSet = prevWinPosSetRef.current;
        const reelIndices = GAME_LAYOUT.REEL_INDICES;

        if (prevSet) {
          // Show textures for positions that WERE winning but no longer are
          for (const prevKey of prevSet) {
            if (!winPosSet.has(prevKey)) {
              const [reel, symbolIndex] = prevKey.split("_").map(Number);
              const reelController =
                reelsetControllerRef.current.reelControllers[reel];
              if (reelController?.showSymbolTexture) {
                reelController.showSymbolTexture(symbolIndex);
              }
            }
          }
        } else {
          // First line — show all non-winning textures (full reset)
          for (let ri = 0; ri < reelIndices.length; ri++) {
            const reel = reelIndices[ri];
            const reelController =
              reelsetControllerRef.current.reelControllers[reel];
            if (!reelController) continue;

            for (
              let symbolIndex = 0;
              symbolIndex < GAME_LAYOUT.SYMBOLS_VISIBLE;
              symbolIndex++
            ) {
              if (!winPosSet.has(`${reel}_${symbolIndex}`)) {
                if (reelController.showSymbolTexture) {
                  reelController.showSymbolTexture(symbolIndex);
                }
              }
            }
          }
        }

        if (!activityRef.current) {
          return;
        }

        // Sync win animations and hide textures for current winning positions
        const positions = data.positions;
        for (let i = 0; i < positions.length; i++) {
          const [reelIndex, symbolIndex] = positions[i];
          const overlayReelController =
            reelsetControllerRef.current.overlayReelControllers[reelIndex];
          const reelController =
            reelsetControllerRef.current.reelControllers[reelIndex];

          if (!overlayReelController || !reelController) continue;

          const hasExpandingWildFullReel =
            FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
            FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel" &&
            overlayReelController.isExpandingWildVisible === true;

          if (hasExpandingWildFullReel) {
            continue;
          }

          // CRITICAL: Read from spinningSprites (which has wild replacements)
          const VISIBLE_SYMBOL_START_INDEX = 1 + GAME_LAYOUT.SYMBOLS_VISIBLE;
          const spriteIndex = VISIBLE_SYMBOL_START_INDEX + symbolIndex;
          const sprite = reelController.spinningSprites?.[spriteIndex];

          if (sprite) {
            const actualSymbolId = sprite.currentSymbolId;
            if (actualSymbolId !== undefined) {
              overlayReelController.syncWinAnimation(
                symbolIndex,
                actualSymbolId,
              );
            }
          }

          if (reelController.hideSymbolTexture) {
            reelController.hideSymbolTexture(symbolIndex);
          }
        }

        // Store current positions for next line's diff
        prevWinPosSetRef.current = winPosSet;

        if (
          FEATURES_CONFIG.WIN_LINES.ENABLED &&
          winLinesContainerRef.current &&
          data.lineId !== undefined &&
          data.lineId !== null
        ) {
          winLinesContainerRef.current.showWinLine(data.lineId);
        }

        // Show win boxes on ALL positions of this line (full line path)
        if (
          FEATURES_CONFIG.WIN_BOX.ENABLED &&
          winBoxContainerRef.current &&
          data.line
        ) {
          const fullLinePositions = data.line.map((row, reelIndex) => [
            reelIndex,
            row,
          ]);
          winBoxContainerRef.current.showWinBoxes(fullLinePositions);
        }

        await new Promise((resolve) => {
          // Compute master spine duration once and cache it
          if (masterSpineDurationRef.current === null) {
            const SYMBOL_PRIORITY = {
              wild: 4,
              scatter: 3,
              hp: 2,
              mp: 1,
              lp: 0,
            };
            let bestPriority = -1;
            let bestDuration =
              FEATURES_CONFIG.WIN_ANIMATION_TIMING.SPINE_LOOP_DURATION;

            // Search all overlay reel controllers for the highest-priority symbol's win animation duration
            const overlayControllers =
              reelsetControllerRef.current?.overlayReelControllers;
            if (overlayControllers) {
              for (const overlayReelController of Object.values(
                overlayControllers,
              )) {
                if (!overlayReelController?.prePositionedSpines) continue;
                for (const [
                  spineKey,
                  spine,
                ] of overlayReelController.prePositionedSpines) {
                  if (!spine?.state) continue;
                  // Extract symbolId from spineKey (format: "symbolIndex_symbolId")
                  const parts = spineKey.split("_");
                  const symId = parseInt(parts[1]);
                  const symData = symbolData[symId];
                  const priority = symData
                    ? (SYMBOL_PRIORITY[symData.symbolPay] ?? 0)
                    : 0;

                  if (priority > bestPriority) {
                    // Find the "win" animation duration from this spine
                    const animations = spine.skeleton?.data?.animations;
                    if (animations) {
                      const winAnim = animations.find((a) =>
                        a.name.toLowerCase().includes("win"),
                      );
                      if (winAnim) {
                        bestPriority = priority;
                        bestDuration = winAnim.duration * 1000; // Convert seconds to ms
                      }
                    }
                  }
                }
              }
            }
            masterSpineDurationRef.current = bestDuration;
          }

          // Use cached master duration × loop count for all lines
          const targetLoops =
            FEATURES_CONFIG.WIN_ANIMATION_TIMING.LOOPS_PER_LINE || 1;
          const lineDuration = masterSpineDurationRef.current * targetLoops;

          // Play win_line a second time at half the line duration
          if (
            FEATURES_CONFIG.WIN_LINES.ENABLED &&
            SOUND_NAME.win_line &&
            data.lineId !== undefined &&
            data.lineId !== null
          ) {
            const secondPlayTimeout = setTimeout(() => {
              soundManager.play(SOUND_NAME.win_line);
            }, lineDuration / 2);
            currentTimeoutsRef.current.push(secondPlayTimeout);
          }

          // FREE SPIN TRIGGER: Show intro after scatter line animation finishes
          if (
            FEATURES_CONFIG.FREE_SPINS.ENABLED &&
            data.isScatter &&
            store.getState().featureGame.freeSpinTriggered
          ) {
            // If animated win is still showing (4-5 scatters), wait for it to close first
            const animState = store.getState();
            if (selectWinAnimationsPlaying(animState)) {
              const waitForAnimatedWin = new Promise((resolve) => {
                const checkInterval = setInterval(() => {
                  const currentState = store.getState();
                  if (!selectWinAnimationsPlaying(currentState)) {
                    const idx =
                      currentIntervalsRef.current.indexOf(checkInterval);
                    if (idx > -1) currentIntervalsRef.current.splice(idx, 1);
                    clearInterval(checkInterval);
                    resolve();
                  }
                }, 100);
                currentIntervalsRef.current.push(checkInterval);
              });
              waitForAnimatedWin.then(() => {
                dispatchRef.current(setShowFreeSpinIntro(true));
              });
            } else {
              const delayTimeout = setTimeout(() => {
                const idx = currentTimeoutsRef.current.indexOf(delayTimeout);
                if (idx > -1) currentTimeoutsRef.current.splice(idx, 1);
                dispatchRef.current(setShowFreeSpinIntro(true));
              }, lineDuration / 1.25);
              currentTimeoutsRef.current.push(delayTimeout);
            }
          }

          const timeoutId = setTimeout(() => {
            const idx = currentTimeoutsRef.current.indexOf(timeoutId);
            if (idx > -1) currentTimeoutsRef.current.splice(idx, 1);
            resolve();
          }, lineDuration);
          currentTimeoutsRef.current.push(timeoutId);
        });

        // After the first line's animation completes, fire the callback to unblock spin
        if (index === 0 && onFirstLineComplete) {
          onFirstLineComplete();
          onFirstLineComplete = null; // Only fire once
        }

        if (!activityRef.current) {
          return;
        }
      }
    },
    [
      winLinesContainerRef,
      winBoxContainerRef,
      globalLineWinTextRef,
      overlayReelRefs,
      lineWinTextRef,
    ],
  );

  /**
   * Shows all winning symbols simultaneously before line cycling begins.
   * Creates a celebration effect by displaying all winning animations at once.
   */
  const showAllWinningSymbolsOnce = useCallback(
    async (
      winningData,
      hasExpandingWilds,
      expandingWildReelIndices = [],
      winType = null,
      isAnimatedWin = false,
    ) => {
      const allPositions = [];
      const lineIds = Object.keys(winningData);

      lineIds.forEach((lineId) => {
        const data = winningData[lineId];
        data.positions.forEach((position) => {
          const [reelIndex, symbolIndex] = position;
          const exists = allPositions.some(
            ([r, s]) => r === reelIndex && s === symbolIndex,
          );
          if (!exists) {
            allPositions.push(position);
          }
        });
      });

      // Targeted cleanup: hide landing/idle animations on NON-winning positions only.
      // Build a set of winning positions per reel so we can exclude them.
      const winPositionsByReel = new Map();
      allPositions.forEach(([reelIndex, symbolIndex]) => {
        if (!winPositionsByReel.has(reelIndex)) {
          winPositionsByReel.set(reelIndex, new Set());
        }
        winPositionsByReel.get(reelIndex).add(symbolIndex);
      });

      executeOnOverlayReelControllersRef.current(
        (overlayReelController, reelIndex) => {
          if (overlayReelController) {
            const exceptPositions = winPositionsByReel.get(reelIndex) || null;
            overlayReelController.hideAllAnimations(exceptPositions);
          }
        },
      );

      executeOnReelControllersRef.current((controller) => {
        if (controller && controller.showAllSymbolTextures) {
          controller.showAllSymbolTextures();
        }
      });

      // Play win sound for non-animated wins (once per win, after expanding wilds complete)
      if (!isAnimatedWin && SOUND_NAME.win) {
        soundManager.play(SOUND_NAME.win);
      }

      // === STEP 3: Start spine win animations on winning symbols ===
      for (let i = 0; i < allPositions.length; i++) {
        const [reelIndex, symbolIndex] = allPositions[i];
        const overlayReelController =
          reelsetControllerRef.current.overlayReelControllers[reelIndex];
        const reelController =
          reelsetControllerRef.current.reelControllers[reelIndex];

        if (!overlayReelController || !reelController) continue;

        const hasExpandingWildFullReel =
          FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
          FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel" &&
          overlayReelController.isExpandingWildVisible === true;

        if (hasExpandingWildFullReel) {
          continue;
        }

        const symbol = reelController.symbolsContainer.children[symbolIndex];
        if (symbol) {
          const actualSymbolId = symbol.currentSymbolId || symbol.symbolId;
          if (actualSymbolId !== undefined) {
            overlayReelController.showWinAnimation(symbolIndex, actualSymbolId);
          }
        }
      }

      const isFullReelMode =
        FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel";
      const isCascadeMode =
        FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "cascade";

      if (!isFullReelMode && expandingWildReelIndices.length > 0) {
        expandingWildReelIndices.forEach((reelIndex) => {
          const reelController =
            reelsetControllerRef.current.reelControllers[reelIndex];
          if (reelController && reelController.showAllSymbolTextures) {
            reelController.showAllSymbolTextures();
          }
        });
      }

      // In cascade mode, transition cascaded wild symbols to "win" animation
      // ONLY if they are part of an actual win line (non-winning wilds stay as static texture)
      if (isCascadeMode && expandingWildReelIndices.length > 0) {
        expandingWildReelIndices.forEach((reelIndex) => {
          const overlayReelController =
            reelsetControllerRef.current.overlayReelControllers[reelIndex];
          const reelController =
            reelsetControllerRef.current.reelControllers[reelIndex];

          if (!overlayReelController || !reelController) return;

          const winningPositionsOnReel = winPositionsByReel.get(reelIndex);

          for (
            let symbolIndex = 0;
            symbolIndex < GAME_LAYOUT.SYMBOLS_VISIBLE;
            symbolIndex++
          ) {
            // Only animate wilds that are part of a winning line
            if (!winningPositionsOnReel || !winningPositionsOnReel.has(symbolIndex)) {
              continue;
            }

            const symbol =
              reelController.symbolsContainer.children[symbolIndex];
            if (symbol) {
              const actualSymbolId = symbol.currentSymbolId || symbol.symbolId;

              if (actualSymbolId === WILD_SYMBOL_ID) {
                overlayReelController.showWinAnimation(
                  symbolIndex,
                  actualSymbolId,
                  "win",
                );
              }
            }
          }
        });
      }

      // === STEP 4: Hide textures behind spines (reveal spine animations) ===
      for (let i = 0; i < allPositions.length; i++) {
        const [reelIndex, symbolIndex] = allPositions[i];
        const reelController =
          reelsetControllerRef.current.reelControllers[reelIndex];
        const overlayReelController =
          reelsetControllerRef.current.overlayReelControllers[reelIndex];
        const hasExpandingWildFullReel =
          overlayReelController &&
          FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
          FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel" &&
          overlayReelController.isExpandingWildVisible === true;

        if (hasExpandingWildFullReel) {
          continue;
        }

        if (reelController && reelController.hideSymbolTexture) {
          reelController.hideSymbolTexture(symbolIndex);
        }
      }

      // === STEP 5: Show win lines (on top of active spines) ===
      // === STEP 5: Show win lines (on top of active spines) ===
      if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
        const regularLineIds = lineIds
          .map((key) => winningData[key]?.lineId)
          .filter((id) => id !== undefined && id !== null);
        if (regularLineIds.length > 0) {
          winLinesContainerRef.current.showMultipleWinLines(regularLineIds);
        }
      }

      // === STEP 5b: Show win boxes on all line positions ===
      if (FEATURES_CONFIG.WIN_BOX.ENABLED && winBoxContainerRef.current) {
        const allLinePositions = [];
        lineIds.forEach((key) => {
          const data = winningData[key];
          if (data?.line) {
            // Regular line win — convert line pattern to positions
            allLinePositions.push(data.line.map((row, reelIndex) => [reelIndex, row]));
          } else if (data?.positions?.length > 0) {
            // Scatter win (no line) — use actual positions directly
            allLinePositions.push(data.positions);
          }
        });
        if (allLinePositions.length > 0) {
          winBoxContainerRef.current.showAllWinBoxes(allLinePositions);
        }
      }

      // === STEP 6: Show win text (overlay on top of everything) ===
      if (globalLineWinTextRef?.current && currentRoundRef.current?.win) {
        globalLineWinTextRef.current.showWinAmount(currentRoundRef.current.win);
      }

      // === STEP 7: Dim reels LAST (everything is ready, dim reveals the full composition) ===
      if (dimReelSetRef.current) {
        dimReelSetRef.current.dimReels();
      }

      if (
        FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
        FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel" &&
        expandingWildReelIndices.length > 0
      ) {
        expandingWildReelIndices.forEach((reelIndex) => {
          const overlayReelRef = overlayReelRefs.current?.[reelIndex];
          overlayReelRef?.playExpandingWildWinAnimation?.();
          expandingWildAnimationStateRef.current[reelIndex] = true;
        });
      }

      // Event-based timing: use cached master spine duration
      await new Promise((resolve) => {
        // Compute master spine duration once and cache it (same as per-line logic)
        if (masterSpineDurationRef.current === null) {
          const SYMBOL_PRIORITY = { wild: 4, scatter: 3, hp: 2, mp: 1, lp: 0 };
          let bestPriority = -1;
          let bestDuration =
            FEATURES_CONFIG.WIN_ANIMATION_TIMING.SPINE_LOOP_DURATION;

          const overlayControllers =
            reelsetControllerRef.current?.overlayReelControllers;
          if (overlayControllers) {
            for (const overlayReelController of Object.values(
              overlayControllers,
            )) {
              if (!overlayReelController?.prePositionedSpines) continue;
              for (const [
                spineKey,
                spine,
              ] of overlayReelController.prePositionedSpines) {
                if (!spine?.state) continue;
                const parts = spineKey.split("_");
                const symId = parseInt(parts[1]);
                const symData = symbolData[symId];
                const priority = symData
                  ? (SYMBOL_PRIORITY[symData.symbolPay] ?? 0)
                  : 0;

                if (priority > bestPriority) {
                  const animations = spine.skeleton?.data?.animations;
                  if (animations) {
                    const winAnim = animations.find((a) =>
                      a.name.toLowerCase().includes("win"),
                    );
                    if (winAnim) {
                      bestPriority = priority;
                      bestDuration = winAnim.duration * 1000;
                    }
                  }
                }
              }
            }
          }
          masterSpineDurationRef.current = bestDuration;
        }

        const targetLoops = hasExpandingWilds
          ? FEATURES_CONFIG.EXPANDING_WILDS.LOOPS_FOR_EXPANDING_WILD_SHOW_ALL ||
            1
          : FEATURES_CONFIG.SHOW_ALL_WINS.LOOPS_FOR_SHOW_ALL || 1;
        const displayDuration = masterSpineDurationRef.current * targetLoops;

        const timeoutId = setTimeout(() => {
          const index = currentTimeoutsRef.current.indexOf(timeoutId);
          if (index > -1) {
            currentTimeoutsRef.current.splice(index, 1);
          }
          resolve();
        }, displayDuration);
        currentTimeoutsRef.current.push(timeoutId);
      });

      // Dispatch that counter animation has finished (spin unblocking)
      dispatch(setCounterAnimationFinished(true));

      // Release the early animation lock so startWinningLinesAnimation can take over
      isAnimatingRef.current = false;

      if (turboActiveRef.current) {
        return false;
      }

      return true;
    },
    [
      globalLineWinTextRef,
      dimReelSetRef,
      winLinesContainerRef,
      winBoxContainerRef,
      overlayReelRefs,
      dispatch,
    ],
  );

  /**
   * Shows all winning symbols for Hold and Win trigger.
   * Similar to showAllWinningSymbolsOnce but unskippable and doesn't prepare for line cycling.
   */
  const showAllWinningSymbolsForHoldAndWinTrigger = useCallback(
    async (winningData) => {
      const allPositions = [];
      const lineIds = Object.keys(winningData);

      lineIds.forEach((lineId) => {
        const data = winningData[lineId];
        data.positions.forEach((position) => {
          const [reelIndex, symbolIndex] = position;
          const exists = allPositions.some(
            ([r, s]) => r === reelIndex && s === symbolIndex,
          );
          if (!exists) {
            allPositions.push(position);
          }
        });
      });

      executeOnOverlayReelControllersRef.current((overlayReelController) => {
        overlayReelController?.hideAllAnimations();
      });

      executeOnReelControllersRef.current((controller) => {
        if (controller && controller.showAllSymbolTextures) {
          controller.showAllSymbolTextures();
        }
      });

      if (globalLineWinTextRef?.current && currentRoundRef.current?.win) {
        globalLineWinTextRef.current.showWinAmount(currentRoundRef.current.win);
      }

      if (dimReelSetRef.current) {
        dimReelSetRef.current.dimReels();
      }

      for (let i = 0; i < allPositions.length; i++) {
        const [reelIndex, symbolIndex] = allPositions[i];
        const overlayReelController =
          reelsetControllerRef.current.overlayReelControllers[reelIndex];
        const reelController =
          reelsetControllerRef.current.reelControllers[reelIndex];

        if (!overlayReelController || !reelController) continue;

        const symbol = reelController.symbolsContainer.children[symbolIndex];
        if (symbol) {
          const actualSymbolId = symbol.currentSymbolId || symbol.symbolId;
          if (actualSymbolId !== undefined) {
            overlayReelController.showWinAnimation(symbolIndex, actualSymbolId);
          }
        }
      }

      for (let i = 0; i < allPositions.length; i++) {
        const [reelIndex, symbolIndex] = allPositions[i];
        const reelController =
          reelsetControllerRef.current.reelControllers[reelIndex];

        if (reelController && reelController.hideSymbolTexture) {
          reelController.hideSymbolTexture(symbolIndex);
        }
      }

      // Show all win lines simultaneously
      // Show all win lines simultaneously
      if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
        const regularLineIds = lineIds
          .map((key) => winningData[key]?.lineId)
          .filter((id) => id !== undefined && id !== null);
        if (regularLineIds.length > 0) {
          winLinesContainerRef.current.showMultipleWinLines(regularLineIds);
        }
      }

      // Show all win boxes simultaneously
      if (FEATURES_CONFIG.WIN_BOX.ENABLED && winBoxContainerRef.current) {
        const allLinePositions = [];
        lineIds.forEach((key) => {
          const data = winningData[key];
          if (data?.line) {
            allLinePositions.push(data.line.map((row, reelIndex) => [reelIndex, row]));
          } else if (data?.positions?.length > 0) {
            allLinePositions.push(data.positions);
          }
        });
        if (allLinePositions.length > 0) {
          winBoxContainerRef.current.showAllWinBoxes(allLinePositions);
        }
      }

      if (
        FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
        FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel" &&
        expandingWildReelIndicesRef.current.length > 0
      ) {
        expandingWildReelIndicesRef.current.forEach((reelIndex) => {
          const overlayReelRef = overlayReelRefs.current?.[reelIndex];
          overlayReelRef?.playExpandingWildWinAnimation?.();
          expandingWildAnimationStateRef.current[reelIndex] = true;
        });
      }

      const displayDuration = FEATURES_CONFIG.SHOW_ALL_WINS.DISPLAY_DURATION;

      await new Promise((resolve) => {
        const timeoutId = setTimeout(() => {
          const index = currentTimeoutsRef.current.indexOf(timeoutId);
          if (index > -1) {
            currentTimeoutsRef.current.splice(index, 1);
          }
          resolve();
        }, displayDuration);
        currentTimeoutsRef.current.push(timeoutId);
      });

      if (globalLineWinTextRef?.current) {
        globalLineWinTextRef.current.hide();
      }
      if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
        winLinesContainerRef.current.hideWinLine();
      }
      if (FEATURES_CONFIG.WIN_BOX.ENABLED && winBoxContainerRef.current) {
        winBoxContainerRef.current.hideWinBoxes();
      }
    },
    [
      globalLineWinTextRef,
      dimReelSetRef,
      winLinesContainerRef,
      winBoxContainerRef,
      overlayReelRefs,
    ],
  );

  /**
   * Starts the win line animation cycle.
   * Cycles through each winning line, showing animations and win amounts.
   * For feature rounds, reorders lines to show scatter first.
   */
  const startWinningLinesAnimation = useCallback(
    async (winningData) => {
      if (isAnimatingRef.current) {
        return;
      }

      const lineIds = Object.keys(winningData);
      const winTypeLocal = winType;
      const isAnimatedWinLocal = isAnimatedWin;

      if (winTypeLocal && !winSoundPlayedRef.current) {
        if (isAnimatedWinLocal) {
          winSoundPlayedRef.current = true;
        }
      }

      isAnimatingRef.current = true;
      clearAllTimeouts();

      if (globalLineWinTextRef?.current) {
        globalLineWinTextRef.current.hide();
      }

      // Clear previous win positions for clean first-line diff
      prevWinPosSetRef.current = null;

      if (dimReelSetRef.current) {
        dimReelSetRef.current.dimReels();
      }

      winningDataRef.current = winningData;
      lineIdsRef.current = lineIds.slice();
      currentLineIndexRef.current = 0;

      const runSeamlessAnimationCycle = async () => {
        while (isAnimatingRef.current) {
          try {
            // On first cycle, pass callback to unblock spin after first line completes
            const unblockCallback = isFirstAnimationRef.current
              ? () => dispatchRef.current(setCounterAnimationFinished(true))
              : null;

            await cycleThroughLines(
              winningData,
              lineIds,
              isFirstAnimationRef.current,
              0,
              unblockCallback,
            );

            isFirstAnimationRef.current = false;
            currentLineIndexRef.current = 0;
          } catch (error) {
            break;
          }
        }
      };

      // FREE SPIN TRIGGER: Play lines linearly once (0 → scatter) and stop.
      // Scatter is always the last line, so we end on it naturally.
      if (
        FEATURES_CONFIG.FREE_SPINS.ENABLED &&
        store.getState().featureGame.freeSpinTriggered
      ) {
        // Ensure animated win is tracked — for scatter-only rounds (4-5 scatters),
        // showAllWinningSymbolsOnce is skipped so winAnimationsPlaying was never set.
        // Without this, the scatter detection wait in cycleThroughLines can't catch it.
        if (isAnimatedWin) {
          dispatchRef.current(setWinAnimationsPlaying(true));
        }

        const unblockCallback = isFirstAnimationRef.current
          ? () => dispatchRef.current(setCounterAnimationFinished(true))
          : null;

        await cycleThroughLines(
          winningData,
          lineIds,
          isFirstAnimationRef.current,
          0,
          unblockCallback,
        );
        isFirstAnimationRef.current = false;
        return;
      }

      runSeamlessAnimationCycle();
    },
    [
      clearAllTimeouts,
      cycleThroughLines,
      dimReelSetRef,
      globalLineWinTextRef,
      winType,
      isAnimatedWin,
    ],
  );

  const cycleThroughLinesRef = useRef(null);
  cycleThroughLinesRef.current = cycleThroughLines;

  const showAllWinningSymbolsOnceRef = useRef(null);
  showAllWinningSymbolsOnceRef.current = showAllWinningSymbolsOnce;

  const showAllWinningSymbolsForHoldAndWinTriggerRef = useRef(null);
  showAllWinningSymbolsForHoldAndWinTriggerRef.current =
    showAllWinningSymbolsForHoldAndWinTrigger;

  const startWinningLinesAnimationRef = useRef(null);
  startWinningLinesAnimationRef.current = startWinningLinesAnimation;

  /**
   * Resumes win line animations when app becomes active after being inactive.
   * Continues cycling from where it left off or reconstructs state from current round data.
   */
  useEffect(() => {
    if (!isActive) {
      if (!holdAndWinTriggerSequenceActiveRef.current) {
        clearAllTimeouts();
      }
      return;
    }

    const hasWins = !!(
      currentRoundRef.current?.wins &&
      Object.keys(currentRoundRef.current.wins).length > 0
    );

    if (spinCompleteRef.current && hasWins && !animationCompletedRef.current) {
      let resumeLineIds = lineIdsRef.current;
      if (
        !winningDataRef.current ||
        !resumeLineIds ||
        resumeLineIds.length === 0
      ) {
        const wl = winningLinesRef.current;
        const cr = currentRoundRef.current;
        if (!wl || !cr?.wins) return;
        resumeLineIds = Object.keys(cr.wins);
        if (cr.type === "feature") {
          resumeLineIds.splice(resumeLineIds.indexOf("scatter"), 1);
          resumeLineIds.unshift("scatter");
          resumeLineIds.splice(1);
        }
      }

      let startIndex = Math.min(
        Math.max(currentLineIndexRef.current, 0),
        Math.max((resumeLineIds?.length || 1) - 1, 0),
      );

      if (!isAnimatingRef.current) {
        isAnimatingRef.current = true;
      }

      (async () => {
        // Resume cycling with the same infinite-loop pattern as startWinningLinesAnimation
        while (isAnimatingRef.current) {
          await cycleThroughLinesRef.current(
            winningDataRef.current || {},
            resumeLineIds || [],
            false,
            startIndex,
          );

          // After first resumed cycle, always restart from the beginning
          startIndex = 0;

          // Check if we should stop (turbo activated, or animation cancelled)
          if (!isAnimatingRef.current || turboActiveRef.current) {
            isAnimatingRef.current = false;
            animationCompletedRef.current = true;
            break;
          }
        }
      })();
    }
  }, [isActive, clearAllTimeouts]);

  /**
   * Resets all animations and effects when a new spin starts.
   * Only runs for regular game spins, not Hold and Win spins.
   */
  useEffect(() => {
    if (holdAndWinActive) {
      return;
    }

    if (!isSpinning || !reelsetControllerRef.current) return;

    const wasAnimating = isAnimatingRef.current;

    reelsetControllerRef.current.resetAllEffects();

    // Always clear all timeouts — catches orphaned timeouts from prior cycles
    clearAllTimeouts();

    if (wasAnimating) {
      isAnimatingRef.current = false;
    }

    dispatch(setWinAnimationsPlaying(false));
    dispatch(setIsAnimatedWin(false));
    dispatch(setCriticalAnimationPlaying(false));
    dispatch(setExpandingWildsOccurred(false));
    dispatch(setSkipWinAnimation(false));
    dispatch(setUserWantsToDismissWinAnimation(false));
    dispatch(setSkipCounterAnimation(false));
    dispatch(setIsWaitingForUserConfirmation(false));

    animationCompletedRef.current = false;
    isFirstAnimationRef.current = true;
    animatedRoundsRef.current.clear();
    winSoundPlayedRef.current = false;
    expandingWildReelIndicesRef.current = [];
    expandingWildAnimationStateRef.current = {};
    winningDataRef.current = null;
    lineIdsRef.current = [];
    currentLineIndexRef.current = 0;
    holdAndWinOutroTriggeredRef.current = false;
    masterSpineDurationRef.current = null;

    soundManager.stop(SOUND_NAME.expanding_wild);
    if (FEATURES_CONFIG.WIN_LINES.ENABLED) {
      soundManager.stop(SOUND_NAME.win_line);
    }
  }, [
    isSpinning,
    holdAndWinActive,
    clearAllTimeouts,
    dispatch,
    winLinesContainerRef,
    overlayReelRefs,
    globalLineWinTextRef,
    lineWinTextRef,
  ]);

  /**
   * Resets visual effects when returning from free spins feature.
   */
  useEffect(() => {
    if (resetMainGameVisualEffects) {
      if (reelsetControllerRef.current?.reelControllers) {
        reelsetControllerRef.current.resetAllEffects();

        if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
          winLinesContainerRef.current.hideWinLine();
        }

        if (FEATURES_CONFIG.WIN_BOX.ENABLED && winBoxContainerRef.current) {
          winBoxContainerRef.current.hideWinBoxes();
        }
      }

      if (isAnimatingRef.current) {
        isAnimatingRef.current = false;
        clearAllTimeouts();
      }

      animationCompletedRef.current = false;
      isFirstAnimationRef.current = true;
      animatedRoundsRef.current.clear();
      holdAndWinOutroTriggeredRef.current = false;

      dispatch(setResetMainGameVisualEffects(false));
    }
  }, [
    resetMainGameVisualEffects,
    clearAllTimeouts,
    dispatch,
    winLinesContainerRef,
    winBoxContainerRef,
  ]);

  /**
   * Shows turbo suggestion popup on non-winning rounds when appropriate.
   */
  useEffect(() => {
    if (!FEATURES_CONFIG.TURBO_SUGGESTION.ENABLED) {
      return;
    }

    if (
      turboSuggestionPending &&
      spinComplete &&
      !isSpinning &&
      !freeSpinsActive
    ) {
      if (!hasWins) {
        // Check if user previously declined — don't show again
        const uiSettings = StorageManager.loadUISettings();
        if (!uiSettings.turboSuggestionDeclined) {
          dispatch(openTurboSuggestionPopup());
        }
        dispatch(setTurboSuggestionPending(false));
      }
    }
  }, [
    turboSuggestionPending,
    spinComplete,
    isSpinning,
    freeSpinsActive,
    currentRound,
    dispatch,
    hasWins,
  ]);

  /**
   * Cleanup effect: Clears all timeouts and resets effects when component unmounts.
   */
  useEffect(() => {
    return () => {
      clearAllTimeouts();
      isAnimatingRef.current = false;

      if (reelsetControllerRef.current) {
        reelsetControllerRef.current.resetAllEffects();
      }
    };
  }, [clearAllTimeouts]);

  return {
    reelsetController: reelsetControllerRef.current,
  };
};

export default useWinController;
